import React, { useState, useRef, useEffect } from 'react';
import { icons } from './ui/Icons';
import { useAppContext } from '../context/AppContext';
import { findChatbotMatch } from '../utils/chatbotMatcher';
import {
  chatCategories,
  chatResponses,
  fallbackResponse,
  welcomeMessage,
} from '../data/supportData';

/**
 * Componente: SupportChatbot
 * Chat flutuante de suporte com respostas pré-definidas
 */
const SupportChatbot = () => {
  const { currentUser } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [pendingAmbiguity, setPendingAmbiguity] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll automático para a última mensagem
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Foco no input quando abre o chat
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Escutar evento customizado para abrir chatbot via SupportPage
  useEffect(() => {
    const handler = () => handleOpen();
    window.addEventListener('open-chatbot', handler);
    return () => window.removeEventListener('open-chatbot', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);

  // Mensagem de boas-vindas ao abrir
  const handleOpen = () => {
    setIsOpen(true);
    if (messages.length === 0) {
      setMessages([
        {
          id: Date.now(),
          type: 'bot',
          content: welcomeMessage.message,
          timestamp: new Date(),
        },
      ]);
    }
  };

  // Simular "typing..." antes de responder
  const addBotMessage = (content, delay = 800) => {
    setIsTyping(true);
    setTimeout(() => {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now(),
          type: 'bot',
          content,
          timestamp: new Date(),
        },
      ]);
      setIsTyping(false);
    }, delay);
  };

  // Quando o utilizador seleciona uma categoria
  const handleCategorySelect = (category) => {
    setSelectedCategory(category.id);
    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        type: 'user',
        content: category.label,
        timestamp: new Date(),
      },
    ]);

    const questions = chatResponses[category.id];
    if (questions && questions.length > 0) {
      const questionList = questions
        .map((q, i) => `${i + 1}. ${q.question}`)
        .join('\n');
      addBotMessage(
        `Ótimo! Aqui estão as perguntas mais comuns sobre **${category.label}**:\n\n${questionList}\n\nEscolha um número ou escreva a sua dúvida:`
      );
    }
  };

  // Quando seleciona uma pergunta pelo número
  const handleQuestionSelect = (questionIndex) => {
    const questions = chatResponses[selectedCategory];
    if (questions && questions[questionIndex]) {
      const qa = questions[questionIndex];
      addBotMessage(qa.answer);
    }
  };

  const handleAmbiguityChoice = (category) => {
    setPendingAmbiguity(false);
    setSelectedCategory(category);
    const qa = category === 'client'
      ? chatResponses.client.find(item => item.id === 'client-book')
      : chatResponses.owner.find(item => item.id === 'owner-slots');
    addBotMessage(qa.answer, 300);
  };

  // Enviar mensagem do utilizador
  const handleSend = () => {
    const text = inputValue.trim();
    if (!text) return;

    setMessages((prev) => [
      ...prev,
      {
        id: Date.now(),
        type: 'user',
        content: text,
        timestamp: new Date(),
      },
    ]);
    setInputValue('');

    // Verificar se é um número (seleção de pergunta)
    const num = parseInt(text, 10);
    if (selectedCategory && !isNaN(num) && num >= 1) {
      const questions = chatResponses[selectedCategory];
      if (questions && questions[num - 1]) {
        handleQuestionSelect(num - 1);
        return;
      }
    }

    const result = findChatbotMatch(text, chatResponses, {
      selectedCategory,
      currentRole: currentUser?.role,
    });
    if (result.type === 'match') {
      addBotMessage(result.qa.answer);
    } else if (result.type === 'ambiguous') {
      setPendingAmbiguity(true);
      addBotMessage('Você deseja **agendar um atendimento como cliente** ou **disponibilizar um horário como barbeiro**?');
    } else {
      addBotMessage(fallbackResponse.message);
    }
  };

  // Voltar ao menu principal
  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setPendingAmbiguity(false);
    addBotMessage(
      'Sem problema! Escolha outra categoria ou escreva a sua dúvida:',
      400
    );
  };

  // Reset do chat
  const handleReset = () => {
    setMessages([
      {
        id: Date.now(),
        type: 'bot',
        content: welcomeMessage.message,
        timestamp: new Date(),
      },
    ]);
    setSelectedCategory(null);
    setPendingAmbiguity(false);
  };

  // Formatar texto com **bold** e \n
  const formatMessage = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|\n)/g);
    return parts.map((part, i) => {
      if (part === '\n') return <br key={i} />;
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <strong key={i} className="font-semibold">
            {part.slice(2, -2)}
          </strong>
        );
      }
      return <span key={i}>{part}</span>;
    });
  };

  return (
    <>
      {/* Botão flutuante */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full p-3 sm:p-4 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 group"
          title="Suporte"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
          </svg>
          {/* Badge "Ajuda" */}
          <span className="absolute -top-2 -left-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
            ?
          </span>
        </button>
      )}

      {/* Janela do Chat */}
      {isOpen && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 z-50 w-full sm:w-[380px] sm:max-w-[calc(100vw-2rem)] h-full sm:h-[550px] sm:max-h-[calc(100vh-3rem)] bg-white sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden sm:border sm:border-gray-200">
          {/* Header do Chat */}
          <div className="bg-indigo-600 text-white p-3 sm:p-4 flex items-center justify-between flex-shrink-0 safe-area-top">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
                </svg>
              </div>
              <div>
                <h3 className="font-semibold text-sm">Suporte Barberize</h3>
                <p className="text-xs text-indigo-200">Online • Respostas instantâneas</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={handleReset}
                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                title="Reiniciar conversa"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" />
                </svg>
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 hover:bg-white/20 rounded-full transition-colors"
                title="Fechar"
              >
                {icons.x({ className: 'h-5 w-5' })}
              </button>
            </div>
          </div>

          {/* Área de Mensagens */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 bg-gray-50">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                    msg.type === 'user'
                      ? 'bg-indigo-600 text-white rounded-br-md'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                  }`}
                >
                  {formatMessage(msg.content)}
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-800 shadow-sm border border-gray-100 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Botões de Categorias (se não selecionou nenhuma) */}
            {!selectedCategory && !pendingAmbiguity && messages.length > 0 && !isTyping && (
              <div className="space-y-2">
                {chatCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategorySelect(cat)}
                    className="w-full text-left bg-white hover:bg-indigo-50 border border-gray-200 hover:border-indigo-300 rounded-xl px-4 py-3 text-sm transition-all duration-200 shadow-sm hover:shadow"
                  >
                    <span className="font-medium text-gray-800">{cat.label}</span>
                    <p className="text-xs text-gray-500 mt-0.5">{cat.description}</p>
                  </button>
                ))}
              </div>
            )}

            {/* Botões de perguntas rápidas (se selecionou categoria) */}
            {selectedCategory && !isTyping && messages.length > 1 && (
              <div className="space-y-1.5">
                {chatResponses[selectedCategory]?.map((qa, index) => (
                  <button
                    key={qa.id}
                    onClick={() => {
                      setMessages((prev) => [
                        ...prev,
                        {
                          id: Date.now(),
                          type: 'user',
                          content: qa.question,
                          timestamp: new Date(),
                        },
                      ]);
                      addBotMessage(qa.answer);
                    }}
                    className="w-full text-left bg-white hover:bg-indigo-50 border border-gray-200 hover:border-indigo-300 rounded-lg px-3 py-2 text-xs transition-all duration-200"
                  >
                    <span className="text-indigo-600 font-medium">{index + 1}.</span>{' '}
                    <span className="text-gray-700">{qa.question}</span>
                  </button>
                ))}
                <button
                  onClick={handleBackToCategories}
                  className="w-full text-center text-xs text-indigo-600 hover:text-indigo-800 py-2 font-medium"
                >
                  ← Voltar às categorias
                </button>
              </div>
            )}

            {pendingAmbiguity && !isTyping && (
              <div className="grid grid-cols-1 gap-2">
                <button
                  onClick={() => handleAmbiguityChoice('client')}
                  className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
                >
                  Agendar como cliente
                </button>
                <button
                  onClick={() => handleAmbiguityChoice('owner')}
                  className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
                >
                  Criar horário como barbeiro
                </button>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input de mensagem */}
          <div className="p-3 sm:p-3 border-t border-gray-200 bg-white flex-shrink-0 safe-area-bottom">
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder="Escreva a sua dúvida..."
                className="flex-1 border border-gray-300 rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <button
                onClick={handleSend}
                disabled={!inputValue.trim()}
                className="bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white rounded-full p-2 transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m22 2-7 20-4-9-9-4Z" /><path d="M22 2 11 13" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SupportChatbot;
